// orgChartApiService.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from './loggingService';

const baseUrl = 'https://api.innoworx.ai/Stage';

/**
 * Get authentication token from AWS Amplify
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) return tokens.idToken.toString();
    console.error('No ID Token found. User may not be logged in.');
    return null;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    return null;
  }
};

/**
 * Fetch all organization chart users
 * Uses the existing /user/getall endpoint with fallback to mock data
 */


export const fetchAllOrgChartUsers = async () => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required. Please log in again.');
  }

  try {
    const response = await fetch(`${baseUrl}/user/getall`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      // Optionally, get error text for debugging
      const errorText = await response.text();
      const error = new Error(`Failed to load users: ${response.status} ${response.statusText} - ${errorText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = '/user/getall';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API error: ${response.status} ${response.statusText} for fetchAllOrgChartUsers`,
        error,
        {
          endpoint: '/user/getall',
          method: 'GET',
          status: response.status,
          statusText: response.statusText,
          responseData: errorText,
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
      
      throw error;
    }
  } catch (error) {
    // Log network/fetch errors to logging service
    if (error.name === 'TypeError' || !error.status) {
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API network/fetch error for fetchAllOrgChartUsers`,
        error,
        {
          endpoint: '/user/getall',
          method: 'GET',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
    }
    throw error;
  }
};


/**
 * Fetch all drivers
 * Uses the existing /vehicle/getall endpoint since drivers are associated with vehicles
 */


export const fetchAllDrivers = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  try {
    const res = await fetch(`${baseUrl}/driver/user/getall`, {
      method: 'GET',
      headers: { 
        Authorization: `Bearer ${token}`, 
        'Content-Type': 'application/json' 
      },
    });

    if (!res.ok) {
      const errorBody = await res.text();
      const error = new Error(`HTTP ${res.status}: ${res.statusText} - ${errorBody}`);
      error.status = res.status;
      error.statusText = res.statusText;
      error.endpoint = '/driver/user/getall';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API error: ${res.status} ${res.statusText} for fetchAllDrivers`,
        error,
        {
          endpoint: '/driver/user/getall',
          method: 'GET',
          status: res.status,
          statusText: res.statusText,
          responseData: errorBody,
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await res.json();
    // Assume data is an array of drivers with fields matching Flutter structure
    return data.map(driver => ({
      employee_id: driver.employee_id || driver.id,
      user_id: driver.user_id || driver.employee_id || driver.id, // Preserve user_id for profile image fetching
      user_name: driver.user_name || driver.driver_name || 'Driver',
      spec_role: 'driver',
      role_name: 'driver',
      reports_to: driver.reports_to || [],
      image_path: driver.image_path || '',
    }));
  } catch (error) {
    // Log network/fetch errors to logging service
    if (error.name === 'TypeError' || !error.status) {
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API network/fetch error for fetchAllDrivers`,
        error,
        {
          endpoint: '/driver/user/getall',
          method: 'GET',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
    }
    throw error;
  }
};


/**
 * Get S3 presigned URL for image
 * Uses the existing profile photo endpoint
 */
// export const getS3PresignedUrl = async (imagePath) => {
//   const token = await getAuthToken();
//   if (!token) throw new Error('Authentication required.');

//   try {
//     // For now, return the image path as-is since we don't have a presigned URL endpoint
//     // In a real implementation, you would call the actual presigned URL endpoint
//     if (imagePath && imagePath.startsWith('s3://')) {
//       // Return a placeholder or the original path
//       return imagePath;
//     }
//     return imagePath;
//   } catch (error) {
//     console.error('Error getting presigned URL:', error);
//     return "https://placehold.co/48x48";
//   }
// };


export const getProfilePhoto = async (userId) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  try {
    const res = await fetch(`${baseUrl}/user/profile-picture?user_id=${userId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
      error.status = res.status;
      error.statusText = res.statusText;
      error.endpoint = '/user/profile-picture';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API error: ${res.status} ${res.statusText} for getProfilePhoto`,
        error,
        {
          endpoint: '/user/profile-picture',
          method: 'GET',
          status: res.status,
          statusText: res.statusText,
          userId,
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
      
      throw error;
    }
    const data = await res.json();
    // Return the profile image URL directly (matching employeeApi implementation)
    return data.profile_image_url || data.image_path || data.profile_image_path || '';
  } catch (error) {
    // Log network/fetch errors to logging service
    if (error.name === 'TypeError' || !error.status) {
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API network/fetch error for getProfilePhoto`,
        error,
        {
          endpoint: '/user/profile-picture',
          method: 'GET',
          userId,
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
    }
    throw error;
  }
};

/**
 * Get current user ID
 * Uses tenant info endpoint to get current user info
 */
export const getUserId = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  try {
    const res = await fetch(`${baseUrl}/tenant`, {
      method: 'GET',
      headers: { 
        Authorization: `Bearer ${token}`, 
        'Content-Type': 'application/json' 
      },
    });

    if (!res.ok) {
      const apiError = new Error(`HTTP ${res.status}: ${res.statusText}`);
      apiError.status = res.status;
      apiError.statusText = res.statusText;
      apiError.endpoint = '/tenant';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API error: ${res.status} ${res.statusText} for getUserId`,
        apiError,
        {
          endpoint: '/tenant',
          method: 'GET',
          status: res.status,
          statusText: res.statusText,
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
      
      throw apiError;
    }
    const data = await res.json();
    return data.user_id || data.employee_id || data.id;
  } catch (error) {
    console.error('Error getting user ID:', error);
    
    // Log network/fetch errors to logging service
    if (error.name === 'TypeError' || !error.status) {
      loggingService.logError(
        'api_error_org_chart',
        `Org Chart API network/fetch error for getUserId`,
        error,
        {
          endpoint: '/tenant',
          method: 'GET',
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[orgChartApiService] Failed to log error:', logError);
      });
    }
    
    return null;
  }
};

/**
 * Get image URL with caching
 */
// export class ImageUrlCache {
//   constructor() {
//     this.cache = new Map();
//   }

//   async getImageUrl(imagePathOrUserId) {
//     if (!imagePathOrUserId || imagePathOrUserId === '') {
//       return "https://placehold.co/48x48";
//     }

//     if (this.cache.has(imagePathOrUserId)) {
//       return this.cache.get(imagePathOrUserId);
//     }

//     try {
//       let url;
//       // If input looks like userId (not full URL), call getProfilePhoto
//       if (!imagePathOrUserId.startsWith('http') && !imagePathOrUserId.startsWith('s3://')) {
//         // Treat as userId to get profile photo url
//         url = await getProfilePhoto(imagePathOrUserId);
//       } else {
//         // Otherwise treat as a direct image path or URL
//         url = imagePathOrUserId;
//       }
//       this.cache.set(imagePathOrUserId, url);
//       return url;
//     } catch (error) {
//       console.error('Error getting image URL:', error);
//       return "https://placehold.co/48x48";
//     }
//   }

//   clearCache() {
//     this.cache.clear();
//   }
// }

export default {
  fetchAllOrgChartUsers,
  fetchAllDrivers,
  getUserId,
  
};
